import { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import { useDispatch, useSelector } from "react-redux";
import { withTranslation } from "react-i18next";

import PlanScope from "@plannel/pages/factory-plan/common/component/PlanScope";
import SearchOptsGroup from "@plannel/pages/factory-plan/common/component/SearchOptsGroup";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import reduxUtil from "@plannel/utils/redux-util";

import ItemTab from "@plannel/pages/factory-plan/master/item/ItemTab";
import InventoryTab from "@plannel/pages/factory-plan/master/item/InventoryTab";

// 이 화면의 고정 조회조건 라벨 키(§10 — DB 동적 표시 제어 제거).
const SEARCH_FIELD_LABELS = {
  itemGrpCdNm: "FP_ITEM_GRP",
  itemCdNm: "FP_ITEM",
  stageCdNm: "FP_STAGE",
  inventoryCdNm: "FP_INVENTORY",
};

// old tabSrhOptsInfo 대응 — 탭별로 노출할 조회조건 필드 목록만 다르게(§11-2), 화면(viewName)
// 단위 고정 화이트리스트로 탭 전환 시 노출 필드 셋만 교체.
const TAB_SEARCH_FIELDS = [
  ["itemGrpCdNm", "itemCdNm"],                 // 0: 품목(Item) 탭
  ["stageCdNm", "itemCdNm", "inventoryCdNm"],  // 1: 인벤토리(Inventory) 탭
];

// 다른 화면(validation/Validation) → 품목/인벤토리 탭 자동 전환 + 검색값 프리필(딥링크).
// old: location.state(paramType/paramCode)로 진입 시 해당 탭으로 전환 + SearchOptsGroup.setValue(...).
// new: historyState(redux, 아래 참고)로 대체 — Validation.js가 이 화면의 viewState에 필드를 먼저
// 심어두고(updateViewState) historyState로 신호를 보내면(updateHistoryState), 이 화면은
// historyState가 자기 viewName과 일치하는 순간을 감지해 viewState를 다시 읽어 반영한다. 이미 이
// 탭이 열려 있어(재마운트가 아니라서) 최초 useState 초기값 읽기가 다시 실행 안 되는 경우까지 커버.
const Item = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [planScope, setPlanScope] = useState(reduxViewState?.planScope ?? "");
  const [tabIndex, setTabIndex] = useState(reduxViewState?.tabIndex ?? 0);
  const [searchFields, setSearchFields] = useState({
    itemGrpCdNm: reduxViewState?.itemGrpCdNm ?? "",
    itemCdNm: reduxViewState?.itemCdNm ?? "",
    stageCdNm: reduxViewState?.stageCdNm ?? "",
    inventoryCdNm: reduxViewState?.inventoryCdNm ?? "",
  });

  useEffect(() => {
    const reduxState = reduxUtil.createObjectState(viewName, { planScope, ...searchFields });
    reduxDispatch(reduxUtil.updateViewState(reduxState));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planScope, searchFields]);

  // historyState는 "다른 화면이 나에게 필터를 들고 이동해왔다"는 1회성 신호 전용 채널이다(탭을
  // 직접 클릭해 전환하는 일반적인 경우는 이 채널을 타지 않음 — TabContainer.js의 handleChange는
  // updateTabIndex만 디스패치). 이 화면을 향한 신호일 때만 방금 심어진 viewState를 다시 읽는다.
  const incomingNav = useSelector((state) => state.historyState.history);
  useEffect(() => {
    if (incomingNav !== viewName) return;
    const freshState = reduxUtil.getViewState(viewName);
    if (!freshState) return;

    if (freshState.planScope) setPlanScope(freshState.planScope);
    if (freshState.tabIndex !== undefined) setTabIndex(freshState.tabIndex);
    setSearchFields((prev) => ({
      itemGrpCdNm: freshState.itemGrpCdNm ?? prev.itemGrpCdNm,
      itemCdNm: freshState.itemCdNm ?? prev.itemCdNm,
      stageCdNm: freshState.stageCdNm ?? prev.stageCdNm,
      inventoryCdNm: freshState.inventoryCdNm ?? prev.inventoryCdNm,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingNav]);

  const onSearchFieldChange = (tpCd, value) => setSearchFields(prev => ({ ...prev, [tpCd]: value }));

  return (
    <>
      <FilterContainer>
        <PlanScope value={planScope} onChange={setPlanScope} />
        <SearchOptsGroup
          fields={searchFields}
          fieldKeys={TAB_SEARCH_FIELDS[tabIndex]}
          onChange={onSearchFieldChange}
          labels={SEARCH_FIELD_LABELS}
        />
        {/* FilterContainer는 마지막 자식을 float:right(+필터 토글 아이콘)로 띄운다 — SearchOptsGroup이
            마지막이면 PlanScope와 큰 간격이 생기므로, 빈 Box를 마지막에 둬 그 역할을 넘긴다. */}
        <Box />
      </FilterContainer>
      {/* old(T3)의 컴팩트한 탭 라벨 영역에 맞춰 MUI Tabs 기본 높이/패딩을 축소 — §11 기본값 그대로
          쓰면 라벨 영역이 과도하게 커지고 그리드 위 여백이 늘어난다. */}
      <Tabs
        value={tabIndex}
        onChange={(_, v) => setTabIndex(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ minHeight: 38 }}
      >
        <Tab label={t("FP_ITEM")} sx={{ minHeight: 38, py: 0.75, fontSize: "0.875rem" }} />
        <Tab label={t("FP_INVENTORY")} sx={{ minHeight: 38, py: 0.75, fontSize: "0.875rem" }} />
      </Tabs>
      {/* §11-1: 탭은 항상 마운트하고 display로만 숨긴다 — 그리드 dirty-row/스크롤/선택 상태 보존.
          display:"flex"+flexDirection:"column"+flex:1 로 둬야 #tab-body(flex column)의 남는
          높이를 그대로 이어받아 내부 .grid-area{height:100%}가 정상 동작한다(block이면 무너짐 —
          AG Grid 헤더만 보이고 바디가 0px로 붕괴하는 원인). */}
      <Box sx={{ display: tabIndex === 0 ? "flex" : "none", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <ItemTab viewName={`${viewName}_ITEM`} planScope={planScope} searchFields={searchFields} />
      </Box>
      <Box sx={{ display: tabIndex === 1 ? "flex" : "none", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <InventoryTab viewName={`${viewName}_INVENTORY`} planScope={planScope} searchFields={searchFields} />
      </Box>
    </>
  );
};

export default withTranslation()(Item);
